// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19.
import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useUserData } from '../../hooks/useUserData';
import { useCompanyData } from '../../hooks/useCompanyData';
import { useRefinanceData, RefinanceMatch, OutcomeType, RelationshipType, YearlyRefinanceData } from '../../hooks/useRefinanceData';
import { formatCurrency } from '../../utils/formatters';
import { RefreshCw, CheckCircle, XCircle, AlertCircle, ArrowRight, TrendingUp, BarChart3, Settings, ChevronDown, ChevronUp, BookOpen, ChevronRight, Trash2, Repeat, Phone } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import AcquiredBookPerformanceSection from '../../components/refinance/AcquiredBookPerformanceSection';
import ReviewTabs, { ReviewTab, reviewTabToFilters, PendingRelationship } from '../../components/refinance/ReviewTabs';
import RefinanceStatCards from '../../components/refinance/RefinanceStatCards';
import RefinanceReturningChart from '../../components/refinance/RefinanceReturningChart';
import ManualLinkControl from '../../components/refinance/ManualLinkControl';
import BatchReviewPanel from '../../components/refinance/BatchReviewPanel';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ContactTimeline from '../../components/contacts/ContactTimeline';
import { useLoanContacts } from '../../hooks/useLoanContacts';
import type { MutateContactInput, LoanContactEntry } from '../../hooks/useLoanContacts';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';

interface FileTemplate {
  id: string;
  templateName: string;
  isAcquiredLoanBook: boolean;
}

/**
 * RefinanceIntelligencePage - Track loan book conversions and win-backs
 *
 * Helps companies track:
 * - Cross-loan book conversions (acquired books -> primary)
 * - Broker win-backs (same broker retaining clients)
 * - Internal transfers (different broker, same company)
 */

const RefinanceIntelligencePage: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useUserData();
  const { companyData } = useCompanyData();
  const outletContext = useOutletContext<{ userVisibleTemplates?: any[]; userVisibleOffices?: any[]; loanBooks?: ResolvedLoanBook[] }>();
  const loanBooks = outletContext?.loanBooks ?? [];
  const companyId = userData?.companyId || '';
  // Manual-link affordance is admin-only (founder is additionally blocked
  // server-side). Broker-family and marketing roles do not see the control.
  const canManualLink = ['super_admin', 'administrator', 'office_admin'].includes(userData?.role ?? '');

  const {
    matches,
    metrics,
    detectionResult,
    isLoadingMatches,
    isLoadingMetrics,
    isRunningDetection,
    isVerifying,
    isDeleting,
    error,
    total,
    hasMore,
    fetchMatches,
    fetchMetrics,
    runDetection,
    verifyMatch,
    verifyMatchesBatch,
    bulkVerifyMatches,
    deleteMatch,
    createManualRefinanceMatch,
    loadMore,
  } = useRefinanceData();

  // Batch-review mode: dense multi-select table for clearing the pending backlog
  // (Needs Review tab only). Card view stays the default per-match workflow.
  const [batchMode, setBatchMode] = useState(false);

  // Review tab drives the (status, relationship) filter tuple; replaces the
  // old Status select. 'needs_review' -> pending (matches the prior default).
  const [activeReviewTab, setActiveReviewTab] = useState<ReviewTab>('needs_review');
  // Needs-Review pending sub-filter (All / Refinances / Returning Clients).
  const [pendingRelationship, setPendingRelationship] = useState<PendingRelationship>('all');
  const [acquiredOnlyFilter, setAcquiredOnlyFilter] = useState(false);
  const [templateFilter, setTemplateFilter] = useState<string>('all');

  // Resolve loan book selection → query params (templateId + optional entityCode)
  const resolvedParams = useMemo(() =>
    resolveLoanBookSelection(templateFilter, loanBooks),
    [templateFilter, loanBooks]
  );

  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'confidence_high' | 'confidence_low'>('newest');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [verifyingMatchId, setVerifyingMatchId] = useState<string | null>(null);
  // PER-CARD verify-editor state, keyed by the card's id (splitGroupId for split groups,
  // matchId for singles). MUST be per-card: a single shared value made an edit to one
  // card's outcome/notes appear on — and save to — EVERY card (the verify editor renders
  // on all pending cards at once).
  const [selectedOutcomes, setSelectedOutcomes] = useState<Record<string, OutcomeType>>({});
  // Reviewer's relationship choice per card. Absent key = untouched radio,
  // which pre-selects from the match's suggestedRelationshipType (fallback
  // 'refinance'). The pre-selection counts as an explicit choice on confirm.
  const [selectedRelationships, setSelectedRelationships] = useState<Record<string, RelationshipType>>({});
  const [verificationNotesById, setVerificationNotesById] = useState<Record<string, string>>({});

  // Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; matchId: string | null }>({
    isOpen: false,
    matchId: null,
  });

  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const [templates, setTemplates] = useState<FileTemplate[]>([]);
  const [primaryTemplate, setPrimaryTemplate] = useState<FileTemplate | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Feature flag for template filter (only for companies with multiple templates)
  const TEMPLATE_FILTER_ENABLED_COMPANIES = ['entourage-1766030715599'];
  const showTemplateFilter = TEMPLATE_FILTER_ENABLED_COMPANIES.includes(companyId) && templates.length > 1;

  // Update primary template for the company
  const handlePrimaryTemplateChange = async (templateId: string) => {
    if (!companyId || savingSettings) return;
    setSavingSettings(true);
    try {
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, { primaryTemplateId: templateId || null });

      // Update local state
      const template = templates.find(t => t.id === templateId);
      setPrimaryTemplate(template || null);
    } catch (err) {
      console.error('Failed to update primary template:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // Toggle acquired status for a template
  const handleAcquiredToggle = async (templateId: string, isAcquired: boolean) => {
    if (savingSettings) return;
    setSavingSettings(true);
    try {
      const templateRef = doc(db, 'fileTemplates', templateId);
      await updateDoc(templateRef, { isAcquiredLoanBook: isAcquired });

      // Update local state
      setTemplates(prev => prev.map(t =>
        t.id === templateId ? { ...t, isAcquiredLoanBook: isAcquired } : t
      ));
    } catch (err) {
      console.error('Failed to update template acquired status:', err);
      toast.error('Failed to update acquired status. You may not have permission.');
    } finally {
      setSavingSettings(false);
    }
  };
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Derive loan books from outlet context (office-scoped for office_admin)
  useEffect(() => {
    const visibleTemplates = outletContext?.userVisibleTemplates;
    if (!visibleTemplates || visibleTemplates.length === 0) return;

    setLoadingTemplates(true);
    try {
      const primaryTemplateId = companyData?.primaryTemplateId;
      const templateList: FileTemplate[] = [];
      let foundPrimary: FileTemplate | null = null;

      for (const t of visibleTemplates) {
        const template: FileTemplate = {
          id: t.id,
          templateName: t.templateName || t.name || 'Unknown',
          isAcquiredLoanBook: t.isAcquiredLoanBook || false,
        };
        templateList.push(template);

        if (t.id === primaryTemplateId) {
          foundPrimary = template;
        }
      }

      setTemplates(templateList);
      setPrimaryTemplate(foundPrimary);
    } finally {
      setLoadingTemplates(false);
    }
  }, [outletContext?.userVisibleTemplates, companyData?.primaryTemplateId]);

  // Reset template selection when office changes (template may not exist for new office)
  useEffect(() => {
    setTemplateFilter('all');
  }, [selectedOffice]);

  // Initial data fetch - sortBy is passed to backend for proper pagination
  useEffect(() => {
    if (companyId) {
      const tabFilters = reviewTabToFilters(activeReviewTab, pendingRelationship);
      fetchMatches(companyId, {
        status: tabFilters.status,
        relationship: tabFilters.relationship,
        templateId: templateFilter !== 'all' ? templateFilter : undefined,
        officeId: selectedOffice !== 'all' ? selectedOffice : undefined,
        brokerId: selectedBroker !== 'all' ? selectedBroker : undefined,
        isAcquiredToPrimary: acquiredOnlyFilter || undefined,
        entityCode: resolvedParams.entityCode,
        sortBy,
      });
      fetchMetrics(companyId, {
        officeId: selectedOffice !== 'all' ? selectedOffice : undefined,
        brokerId: selectedBroker !== 'all' ? selectedBroker : undefined,
        entityCode: resolvedParams.entityCode,
      });
    }
  }, [companyId, activeReviewTab, pendingRelationship, templateFilter, acquiredOnlyFilter, sortBy, selectedOffice, selectedBroker, resolvedParams.entityCode, fetchMatches, fetchMetrics]);

  const handleRunDetection = async () => {
    if (!companyId) return;
    try {
      await runDetection(companyId); // pairing window is fixed server-side (DISCOVERY_WINDOW)
    } catch (err) {
      console.error('Detection failed:', err);
    }
  };

  const handleVerify = async (matchId: string, status: 'confirmed' | 'rejected' | 'pending') => {
    if (!companyId) return;
    // Explicit reviewer relationship on confirm. Untouched radio = the
    // pre-selection (detector suggestion, fallback 'refinance').
    const match = matches.find((m) => m.matchId === matchId);
    const relationship: RelationshipType | null =
      status === 'confirmed'
        ? (selectedRelationships[matchId] ?? match?.suggestedRelationshipType ?? 'refinance')
        : null;
    try {
      await verifyMatch(
        matchId,
        companyId,
        status,
        relationship,
        // outcomeType only applies to refinance confirms; returning-client
        // confirms carry no outcome.
        status === 'confirmed' && relationship === 'refinance'
          ? (selectedOutcomes[matchId] ?? 'win_back')
          : undefined,
        verificationNotesById[matchId] || undefined
      );
      setVerifyingMatchId(null);
      setVerificationNotesById((prev) => { const next = { ...prev }; delete next[matchId]; return next; });
      toast.success(
        status === 'confirmed'
          ? 'Match confirmed successfully'
          : status === 'rejected'
          ? 'Match rejected successfully'
          : 'Match reopened for review'
      );
    } catch (err) {
      console.error('Verification failed:', err);
      toast.error(
        err instanceof Error
          ? `Verification failed: ${err.message}`
          : 'Verification failed'
      );
    }
  };

  const handleDelete = (matchId: string) => {
    setDeleteConfirm({ isOpen: true, matchId });
  };

  const confirmDelete = async () => {
    if (!companyId || !deleteConfirm.matchId) return;

    try {
      await deleteMatch(deleteConfirm.matchId, companyId, 'Deleted via UI - incorrect match data');
      toast.success('Match deleted. Re-run detection to create updated match.');
    } catch (err) {
      console.error('Deletion failed:', err);
      toast.error(
        err instanceof Error
          ? `Deletion failed: ${err.message}`
          : 'Deletion failed'
      );
    } finally {
      setDeleteConfirm({ isOpen: false, matchId: null });
    }
  };

  const isLoading = isLoadingMatches || isLoadingMetrics;

  // Group matches by splitGroupId for split loan display
  // Split loans are multiple discharged loans refinanced into one new loan
  const groupedMatches = useMemo(() => {
    const groups: Map<string, RefinanceMatch[]> = new Map();
    const singles: RefinanceMatch[] = [];

    matches.forEach(match => {
      if (match.splitGroupId) {
        const existing = groups.get(match.splitGroupId) || [];
        existing.push(match);
        groups.set(match.splitGroupId, existing);
      } else {
        singles.push(match);
      }
    });

    // Sort within groups by position (largest discharged balance first)
    groups.forEach((groupMatches) => {
      groupMatches.sort((a, b) => a.splitGroupPosition - b.splitGroupPosition);
    });

    return { groups, singles };
  }, [matches]);

  // Derive available template IDs from match data (filters Loan Book dropdown by office/broker)
  const availableTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    matches.forEach(m => {
      if (m.discharged.templateId) ids.add(m.discharged.templateId);
      if (m.newLoan.templateId) ids.add(m.newLoan.templateId);
    });
    return ids;
  }, [matches]);

  // Filter templates to only those present in current match data
  const filteredTemplates = useMemo(() => {
    if (availableTemplateIds.size === 0) return templates;
    return templates.filter(t => availableTemplateIds.has(t.id));
  }, [templates, availableTemplateIds]);

  // Derive available brokers from match data for dropdown
  const availableBrokers = useMemo(() => {
    const brokerMap = new Map<string, string>();
    matches.forEach(m => {
      if (m.discharged.brokerId && m.discharged.brokerDisplay) {
        brokerMap.set(m.discharged.brokerId, m.discharged.brokerDisplay);
      }
      if (m.newLoan.brokerId && m.newLoan.brokerDisplay) {
        brokerMap.set(m.newLoan.brokerId, m.newLoan.brokerDisplay);
      }
    });
    return Array.from(brokerMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [matches]);

  // Canonical loan-contacts store (dual-reads canonical + legacy collections,
  // merged + deduped by id). Writes go through the audited mutateLoanContact
  // callable, which server-stamps the authoritative actor.
  const { contactsByLoan, mutateContact } = useLoanContacts(companyId);

  // Actor context for ContactTimeline. The server re-stamps the authoritative
  // actor on every write; this only drives control-visibility + the "Made by"
  // default in the composer.
  const currentUser = useMemo(
    () => ({
      uid: userData?.uid,
      displayName: userData?.commissionDataName || userData?.displayName,
      email: userData?.email,
      role: userData?.role,
    }),
    [userData?.uid, userData?.commissionDataName, userData?.displayName, userData?.email, userData?.role]
  );

  // Handler for verifying all matches in a split loan group
  const handleVerifyGroup = async (groupMatches: RefinanceMatch[], status: 'confirmed' | 'rejected' | 'pending') => {
    if (!companyId) return;
    const groupId = groupMatches[0].splitGroupId;
    // Explicit reviewer relationship on confirm - one choice for the whole
    // group. Untouched radio = the pre-selection (detector suggestion on the
    // primary portion, fallback 'refinance').
    const relationship: RelationshipType | null =
      status === 'confirmed'
        ? (selectedRelationships[groupId] ?? groupMatches[0].suggestedRelationshipType ?? 'refinance')
        : null;
    try {
      // Verify every portion in the group in ONE wall-clock round-trip — using THIS group's
      // own editor state. Sequential per-portion verify was the "confirm lag" (N callables +
      // N metrics queries in series); the batch path parallelizes and refreshes metrics once.
      await verifyMatchesBatch(
        groupMatches.map((match) => match.matchId),
        companyId,
        status,
        relationship,
        // outcomeType only applies to refinance confirms; returning-client
        // confirms carry no outcome.
        status === 'confirmed' && relationship === 'refinance'
          ? (selectedOutcomes[groupId] ?? 'win_back')
          : undefined,
        verificationNotesById[groupId] || undefined
      );
      setVerifyingMatchId(null);
      setVerificationNotesById((prev) => { const next = { ...prev }; delete next[groupId]; return next; });
      toast.success(
        status === 'confirmed'
          ? `Split loan group (${groupMatches.length} loans) confirmed`
          : status === 'rejected'
          ? `Split loan group (${groupMatches.length} loans) rejected`
          : `Split loan group (${groupMatches.length} loans) reopened for review`
      );
    } catch (err) {
      console.error('Group verification failed:', err);
      toast.error(
        err instanceof Error
          ? `Verification failed: ${err.message}`
          : 'Verification failed'
      );
    }
  };

  if (!companyId) {
    return (
      <div>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading company data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Repeat}
        title="Client Retention"
        subtitle="Track loan book conversions and client retention"
        actions={
          /* Hide Run Detection for office_admin — detection is a company-wide operation */
          userData?.role !== 'office_admin' ? (
            <button
              onClick={handleRunDetection}
              disabled={isRunningDetection}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${isRunningDetection ? 'animate-spin' : ''}`} />
              {isRunningDetection ? 'Running Detection...' : 'Run Detection'}
            </button>
          ) : undefined
        }
      />

      {/* Settings Panel — hidden for office_admin (can't change company-wide template config) */}
      {userData?.role !== 'office_admin' && <div className="bg-gray-800 rounded-lg border border-gray-700">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-750 transition-colors rounded-lg"
        >
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-400" />
            <span className="text-white font-medium">Loan Book Configuration</span>
            {primaryTemplate && (
              <span className="text-gray-400 text-sm ml-2">
                Primary: {primaryTemplate.templateName}
              </span>
            )}
          </div>
          {showSettings ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {showSettings && (
          <div className="px-4 pb-4 border-t border-gray-700">
            {loadingTemplates ? (
              <div className="py-4 text-center text-gray-400">Loading templates...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* Primary Loan Book */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-green-400" />
                    <h4 className="text-green-400 font-medium">Primary Loan Book</h4>
                  </div>
                  <select
                    value={primaryTemplate?.id || ''}
                    onChange={(e) => handlePrimaryTemplateChange(e.target.value)}
                    disabled={savingSettings}
                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    <option value="">-- Select Primary Loan Book --</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.templateName}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Conversions from acquired loan books to this book are tracked as "Acquired to Primary"
                  </p>
                </div>

                {/* Acquired Loan Books */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-purple-400" />
                    <h4 className="text-purple-400 font-medium">Acquired Loan Books</h4>
                  </div>
                  {templates.length > 0 ? (
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                      {templates.map((template) => (
                        <li key={template.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`acquired-${template.id}`}
                            checked={template.isAcquiredLoanBook}
                            onChange={(e) => handleAcquiredToggle(template.id, e.target.checked)}
                            disabled={savingSettings || template.id === primaryTemplate?.id}
                            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500 disabled:opacity-50"
                          />
                          <label
                            htmlFor={`acquired-${template.id}`}
                            className={`text-sm ${template.id === primaryTemplate?.id ? 'text-gray-500' : 'text-white'}`}
                          >
                            {template.templateName}
                            {template.id === primaryTemplate?.id && (
                              <span className="text-xs text-green-400 ml-1">(Primary)</span>
                            )}
                          </label>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400 text-sm">No loan books available</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Check loan books that were purchased or acquired from another business
                  </p>
                </div>
              </div>
            )}

            {/* All Templates Summary */}
            {!loadingTemplates && templates.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-xs text-gray-400">
                  <span className="text-white font-medium">{templates.length}</span> loan book{templates.length !== 1 ? 's' : ''} configured •
                  <span className="text-green-400 ml-1">{primaryTemplate ? 1 : 0} primary</span> •
                  <span className="text-purple-400 ml-1">{templates.filter(t => t.isAcquiredLoanBook).length} acquired</span> •
                  <span className="text-gray-400 ml-1">{templates.filter(t => !t.isAcquiredLoanBook && t.id !== primaryTemplate?.id).length} other</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>}

      {/* Detection Result Banner */}
      {detectionResult && (
        <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <h3 className="text-green-300 font-semibold">Detection Complete</h3>
              <p className="text-green-200 text-sm mt-1">
                Scanned {detectionResult.recordsScanned.toLocaleString()} records.
                Found {detectionResult.matchesFound} matches
                ({detectionResult.newMatchesInserted} new, {detectionResult.existingMatchesSkipped} existing).
                Duration: {(detectionResult.durationMs / 1000).toFixed(1)}s
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <h3 className="text-red-300 font-semibold">Error</h3>
              <p className="text-red-200 text-sm mt-1">{error.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Cards - Client Retention (WS3), three rows */}
      <RefinanceStatCards
        metrics={metrics}
        acquiredOnlyFilter={acquiredOnlyFilter}
        onToggleAcquiredOnly={() => setAcquiredOnlyFilter((prev) => !prev)}
        onNeedsReviewClick={() => setActiveReviewTab('needs_review')}
      />

      {/* Year-over-Year Performance Chart */}
      {metrics?.yearlyBreakdown && metrics.yearlyBreakdown.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Year-over-Year Performance</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[...metrics.yearlyBreakdown].sort((a, b) => a.year - b.year)}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="year" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#F9FAFB' }}
                  cursor={{ fill: 'rgba(55, 65, 81, 0.3)' }}
                  formatter={(value: number, name: string) => {
                    if (name === 'retentionRate') return [`${value}%`, 'Retention Rate'];
                    if (name === 'totalMatches') return [value, 'Refinances'];
                    if (name === 'totalDischarges') return [value, 'Discharges'];
                    if (name === 'acquiredToPrimary') return [value, 'Acquired to Primary'];
                    return [value, name];
                  }}
                />
                <Legend
                  wrapperStyle={{ color: '#9CA3AF' }}
                  formatter={(value) => {
                    if (value === 'totalDischarges') return 'Discharges';
                    if (value === 'totalMatches') return 'Refinances';
                    if (value === 'acquiredToPrimary') return 'Acquired to Primary';
                    return value;
                  }}
                />
                <Bar dataKey="totalDischarges" fill="#6B7280" name="totalDischarges" />
                <Bar dataKey="totalMatches" fill="#22D3EE" name="totalMatches" />
                <Bar dataKey="acquiredToPrimary" fill="#A78BFA" name="acquiredToPrimary" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[...metrics.yearlyBreakdown].sort((a, b) => a.year - b.year).slice(-4).map((year) => (
              <div key={year.year} className="bg-gray-900 rounded-lg p-3">
                <p className="text-xs text-gray-400">{year.year}</p>
                <p className="text-lg font-bold text-cyan-400">{year.retentionRate}%</p>
                <p className="text-xs text-gray-500">Retention Rate</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Refinances vs Returning Clients by year (WS3) */}
      <RefinanceReturningChart yearlyBreakdown={metrics?.yearlyBreakdown} />

      {/* Acquired Book Performance Section */}
      {templates.filter(t => t.isAcquiredLoanBook).length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-400" />
              <h2 className="text-lg font-semibold text-white">Acquired Book Performance</h2>
            </div>
            <button
              onClick={() => navigate('/reports/acquired-books')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors text-sm"
            >
              View All by Office
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <AcquiredBookPerformanceSection
            companyId={companyId}
            primaryTemplate={primaryTemplate}
            acquiredTemplates={templates.filter(t => t.isAcquiredLoanBook)}
          />
        </div>
      )}

      {/* Client Retention review tabs (WS3): drive the (status, relationship) filter */}
      <ReviewTabs
        activeTab={activeReviewTab}
        onTabChange={setActiveReviewTab}
        counts={{
          needsReview: metrics?.pendingVerification ?? 0,
          refinances: Math.max(0, (metrics?.confirmed ?? 0) - (metrics?.returningClients ?? 0)),
          returning: metrics?.returningClients ?? 0,
          rejected: metrics?.rejected ?? 0,
        }}
        pendingRelationship={pendingRelationship}
        onPendingRelationshipChange={setPendingRelationship}
      />

      {/* Match-level vs book-level retention (WS3 help). The broker scorecard's
          acquired-book retention is a different, book-level metric and is not edited here. */}
      <p className="text-xs text-gray-500">
        This page tracks match-level retention: individual discharged loans linked to a confirmed
        refinance or a returning client. It is distinct from the broker scorecard's book-level
        acquired-book retention.
      </p>

      {/* Manual link affordance, Needs Review only (WS3) */}
      {activeReviewTab === 'needs_review' && (
        <ManualLinkControl
          companyId={companyId}
          canManualLink={canManualLink}
          onCreate={createManualRefinanceMatch}
        />
      )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-cyan-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="confidence_high">Highest Confidence</option>
              <option value="confidence_low">Lowest Confidence</option>
            </select>
          </div>
          {userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && outletContext?.userVisibleOffices && outletContext.userVisibleOffices.length > 1 && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Office</label>
              <select
                value={selectedOffice}
                onChange={(e) => { setSelectedOffice(e.target.value); setSelectedBroker('all'); }}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-cyan-500"
              >
                <option value="all">All Offices</option>
                {outletContext.userVisibleOffices.map((o: any) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          )}
          {userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Broker</label>
              <select
                value={selectedBroker}
                onChange={(e) => setSelectedBroker(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-cyan-500"
              >
                <option value="all">All Brokers</option>
                {availableBrokers.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}
          {showTemplateFilter && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Loan Book</label>
              <select
                value={templateFilter}
                onChange={(e) => setTemplateFilter(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-cyan-500"
              >
                <option value="all">All Loan Books</option>
                {filteredTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.templateName}
                    {template.isAcquiredLoanBook ? ' (Acquired)' : ''}
                    {template.id === primaryTemplate?.id ? ' (Primary)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="acquiredOnly"
              checked={acquiredOnlyFilter}
              onChange={(e) => setAcquiredOnlyFilter(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-cyan-500 focus:ring-cyan-500"
            />
            <label htmlFor="acquiredOnly" className="text-sm text-gray-300">
              Acquired to Primary only
            </label>
          </div>
          <div className="ml-auto text-sm text-gray-400">
            Showing {matches.length} of {total} matches
          </div>
        </div>
      </div>

      {/* Batch-review mode toggle (Needs Review only) */}
      {activeReviewTab === 'needs_review' && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-400">View:</span>
          <div className="inline-flex rounded-lg overflow-hidden border border-gray-600">
            <button
              type="button"
              onClick={() => setBatchMode(false)}
              className={`px-3 py-1.5 text-sm ${!batchMode ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            >
              Cards
            </button>
            <button
              type="button"
              onClick={() => setBatchMode(true)}
              className={`px-3 py-1.5 text-sm ${batchMode ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            >
              Batch review
            </button>
          </div>
          <span className="text-xs text-gray-500">Clear the pending backlog fast: select many, confirm or reject in one go.</span>
        </div>
      )}

      {batchMode && activeReviewTab === 'needs_review' ? (
        <BatchReviewPanel
          companyId={companyId}
          matches={matches}
          total={total}
          hasMore={hasMore}
          isLoading={isLoading}
          isVerifying={isVerifying}
          onLoadMore={loadMore}
          onBulkVerify={bulkVerifyMatches}
        />
      ) : (
      <>
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" label="Loading refinance data..." />
        </div>
      )}

      {/* Match Cards */}
      {!isLoading && matches.length === 0 && (
        <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
          <RefreshCw className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No Matches Found</h3>
          <p className="text-gray-500 mb-4">
            Click "Run Detection" to scan for refinance matches in your loan book.
          </p>
        </div>
      )}

      {!isLoading && matches.length > 0 && (
        <div className="space-y-4">
          {/* Render grouped split loans first */}
          {Array.from(groupedMatches.groups.values()).map(groupMatches => (
            <SplitLoanGroupCard
              companyId={companyId}
              key={groupMatches[0].splitGroupId}
              matches={groupMatches}
              isExpanded={verifyingMatchId === groupMatches[0].splitGroupId}
              onToggleExpand={() => setVerifyingMatchId(
                verifyingMatchId === groupMatches[0].splitGroupId ? null : groupMatches[0].splitGroupId
              )}
              selectedOutcome={selectedOutcomes[groupMatches[0].splitGroupId] ?? 'win_back'}
              onOutcomeChange={(o) => setSelectedOutcomes((prev) => ({ ...prev, [groupMatches[0].splitGroupId]: o }))}
              selectedRelationship={selectedRelationships[groupMatches[0].splitGroupId] ?? groupMatches[0].suggestedRelationshipType ?? 'refinance'}
              onRelationshipChange={(r) => setSelectedRelationships((prev) => ({ ...prev, [groupMatches[0].splitGroupId]: r }))}
              verificationNotes={verificationNotesById[groupMatches[0].splitGroupId] ?? ''}
              onNotesChange={(n) => setVerificationNotesById((prev) => ({ ...prev, [groupMatches[0].splitGroupId]: n }))}
              onVerifyGroup={handleVerifyGroup}
              isVerifying={isVerifying}
              contactLoanId={groupMatches[0].discharged.loanId}
              contactCustomerName={groupMatches[0].discharged.customerName}
              contactLoanBroker={groupMatches[0].discharged.brokerDisplay || undefined}
              contacts={contactsByLoan[groupMatches[0].discharged.loanId] || []}
              onMutateContact={mutateContact}
              currentUser={currentUser}
              availableBrokers={availableBrokers}
            />
          ))}

          {/* Then render single matches */}
          {groupedMatches.singles.map((match) => (
            <MatchCard
              companyId={companyId}
              key={match.matchId}
              match={match}
              isExpanded={verifyingMatchId === match.matchId}
              onToggleExpand={() => setVerifyingMatchId(
                verifyingMatchId === match.matchId ? null : match.matchId
              )}
              selectedOutcome={selectedOutcomes[match.matchId] ?? 'win_back'}
              onOutcomeChange={(o) => setSelectedOutcomes((prev) => ({ ...prev, [match.matchId]: o }))}
              selectedRelationship={selectedRelationships[match.matchId] ?? match.suggestedRelationshipType ?? 'refinance'}
              onRelationshipChange={(r) => setSelectedRelationships((prev) => ({ ...prev, [match.matchId]: r }))}
              verificationNotes={verificationNotesById[match.matchId] ?? ''}
              onNotesChange={(n) => setVerificationNotesById((prev) => ({ ...prev, [match.matchId]: n }))}
              onVerify={handleVerify}
              onDelete={handleDelete}
              isVerifying={isVerifying}
              isDeleting={isDeleting}
              contactLoanId={match.discharged.loanId}
              contactCustomerName={match.discharged.customerName}
              contactLoanBroker={match.discharged.brokerDisplay || undefined}
              contacts={contactsByLoan[match.discharged.loanId] || []}
              onMutateContact={mutateContact}
              currentUser={currentUser}
              availableBrokers={availableBrokers}
            />
          ))}

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <button
                onClick={loadMore}
                disabled={isLoadingMatches}
                className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium border border-gray-600 flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMatches ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ChevronRight className="w-4 h-4" />
                    Load More ({matches.length} of {total})
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Match"
        message="Delete this match? This cannot be undone. Re-run detection after deletion to create a fresh match with updated data."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, matchId: null })}
      />
    </div>
  );
};

// ============================================================================
// Split Loan Group Card - Groups multiple discharged loans into one refinance
// ============================================================================

interface SplitLoanGroupCardProps {
  companyId: string;
  matches: RefinanceMatch[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedOutcome: OutcomeType;
  onOutcomeChange: (outcome: OutcomeType) => void;
  selectedRelationship: RelationshipType;
  onRelationshipChange: (relationship: RelationshipType) => void;
  verificationNotes: string;
  onNotesChange: (notes: string) => void;
  onVerifyGroup: (matches: RefinanceMatch[], status: 'confirmed' | 'rejected' | 'pending') => void;
  isVerifying: boolean;
  // Contact logging - anchored to the position-1 discharged loan of the group.
  contactLoanId: string;
  contactCustomerName?: string;
  contactLoanBroker?: string;
  contacts: LoanContactEntry[];
  onMutateContact: (input: MutateContactInput) => Promise<{ ok: boolean; entryId: string }>;
  currentUser: { uid?: string; displayName?: string; email?: string; role?: string };
  availableBrokers: { id: string; name: string }[];
}

const SplitLoanGroupCard: React.FC<SplitLoanGroupCardProps> = ({
  companyId,
  matches,
  isExpanded,
  onToggleExpand,
  selectedOutcome,
  onOutcomeChange,
  selectedRelationship,
  onRelationshipChange,
  verificationNotes,
  onNotesChange,
  onVerifyGroup,
  isVerifying,
  contactLoanId,
  contactCustomerName,
  contactLoanBroker,
  contacts,
  onMutateContact,
  currentUser,
  availableBrokers,
}) => {
  // Refinance verification/deletion is default-denied to a broker-
  // restricted admin at the callable chokepoint; disable, don't hide.
  const { isBrokerFilteredAdmin } = useAuth();
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [contactsExpanded, setContactsExpanded] = useState(false);
  const primary = matches[0]; // Largest discharged loan

  // Calculate totals from group
  const totalDischarged = matches.reduce(
    (sum, m) => sum + (m.discharged.balanceAtDischargeCents || 0),
    0
  );
  // For split-to-split (multiple new loans), use group total; otherwise use single new loan amount
  const newLoanAmount = primary.groupNewLoanCount > 1
    ? primary.groupTotalNewLoanCents
    : primary.newLoan.loanAmountCents;
  // Group confidence = minimum confidence of all matches in the group (conservative estimate)
  const groupConfidence = Math.min(...matches.map(m => m.confidenceScore));

  // Determine if all matches have the same status
  const allPending = matches.every(m => m.status === 'pending');
  const allConfirmed = matches.every(m => m.status === 'confirmed');
  const allRejected = matches.every(m => m.status === 'rejected');
  const groupStatus = allConfirmed ? 'confirmed' : allRejected ? 'rejected' : allPending ? 'pending' : 'mixed';

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    mixed: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-purple-500/30 overflow-hidden">
      {/* Card Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-750"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {/* Split Loan indicator - purple to stand out */}
              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs font-medium border border-purple-500/30 flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 3h5v5M8 3H3v5M3 16v5h5M21 16v5h-5M12 8v8M8 12h8" />
                </svg>
                {/* Always show "X discharged → Y new" for clarity */}
                {primary.groupDischargedCount} discharged → {primary.groupNewLoanCount} new
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusColors[groupStatus]}`}>
                {groupStatus.toUpperCase()}
              </span>
              {primary.isAcquiredToPrimary && groupStatus === 'confirmed' && (
                <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs font-medium flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Acquired to Primary
                </span>
              )}
              {/* Confidence badge - shows match quality (0-100%) */}
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                groupConfidence >= 80
                  ? 'bg-green-500/10 text-green-500'
                  : groupConfidence >= 50
                  ? 'bg-yellow-500/10 text-yellow-500'
                  : 'bg-red-500/10 text-red-500'
              }`}>
                {groupConfidence}% confidence
              </span>
            </div>

            {/* Customer Names - Discharged → New */}
            <div className="flex items-center gap-3 text-lg">
              <span className="font-medium text-white">{primary.discharged.customerName}</span>
              <ArrowRight className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-white">{primary.newLoan.customerName}</span>
            </div>

            {/* Loan Amount Comparison - Total Discharged → New Loan(s) */}
            <div className="flex items-center gap-2 mt-2 text-sm">
              <span className="text-gray-400">
                {formatCurrency(totalDischarged)} total discharged
              </span>
              <ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <span className="text-blue-400 font-medium">
                {formatCurrency(newLoanAmount)}{' '}
                {primary.groupNewLoanCount > 1
                  ? `(${primary.groupNewLoanCount} new loans)`
                  : primary.newLoanFacilityCount > 1
                    ? `(${primary.newLoanFacilityCount} facilities)`
                    : 'new loan'}
              </span>
            </div>

            {/* Loan Book Flow with Broker & Office */}
            <div className="flex items-center gap-2 mt-2 text-sm flex-wrap">
              <span className="text-gray-400">
                {primary.discharged.templateName || 'Unknown Loan Book'}
                {(primary.discharged.brokerDisplay || primary.discharged.officeName) && (
                  <span className="text-gray-500 ml-1">
                    ({[primary.discharged.brokerDisplay, primary.discharged.officeName].filter(Boolean).join(' - ')})
                  </span>
                )}
              </span>
              <ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <span className="text-cyan-400 font-medium">
                {primary.newLoan.templateName || 'Unknown Loan Book'}
                {(primary.newLoan.brokerDisplay || primary.newLoan.officeName) && (
                  <span className="text-cyan-300/70 ml-1">
                    ({[primary.newLoan.brokerDisplay, primary.newLoan.officeName].filter(Boolean).join(' - ')})
                  </span>
                )}
              </span>
            </div>

            {/* New Loan Details */}
            <div className="mt-2 text-sm text-gray-400">
              <span className="text-gray-500">New Loan:</span>{' '}
              <span className="text-white font-medium">{primary.newLoan.loanId}</span>
              <span className="mx-2">•</span>
              <span className="text-blue-400">{primary.newLoan.lender || 'Unknown Lender'}</span>
              <span className="mx-2">•</span>
              <span className="text-blue-400 font-medium">{formatCurrency(newLoanAmount)}</span>
            </div>

            {/* Expand/collapse discharged loans */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDetailsExpanded(!detailsExpanded);
              }}
              className="mt-3 text-xs text-gray-400 hover:text-white flex items-center gap-1"
            >
              {detailsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {detailsExpanded ? 'Hide' : 'Show'} {matches.length} discharged loans
            </button>

            {/* Expanded details - list of discharged loans and new loan */}
            {detailsExpanded && (
              <div className="mt-3 space-y-2 border-t border-gray-700 pt-3">
                {/* Discharged Loans Section */}
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Discharged Loans</div>
                {matches.map((match, idx) => (
                  <div key={match.matchId} className="text-sm text-gray-400">
                    <div className="flex justify-between items-center">
                      <span>
                        {idx + 1}. Loan <span className="text-white">{match.discharged.loanId}</span>
                        <span className="text-gray-500 ml-1">
                          ({match.discharged.lender || 'Unknown lender'})
                        </span>
                      </span>
                      <span className="text-gray-300">
                        {formatCurrency(match.discharged.balanceAtDischargeCents || 0)}
                      </span>
                    </div>
                    {(match.discharged.brokerDisplay || match.discharged.officeName) && (
                      <div className="ml-4 text-xs text-gray-500">
                        {[match.discharged.brokerDisplay, match.discharged.officeName].filter(Boolean).join(' • ')}
                      </div>
                    )}
                  </div>
                ))}
                <div className="flex justify-between items-center pt-2 border-t border-gray-700 text-sm font-medium">
                  <span className="text-gray-300">Total Discharged</span>
                  <span className="text-white">{formatCurrency(totalDischarged)}</span>
                </div>

                {/* New Loan(s) Section */}
                <div className="pt-3 border-t border-gray-700">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    {primary.groupNewLoanCount > 1 ? 'New Loans (Refinanced)' : 'New Loan (Refinanced)'}
                  </div>
                  {/* Get unique new loans from matches */}
                  {(() => {
                    const uniqueNewLoans = new Map<string, typeof primary.newLoan>();
                    matches.forEach(m => {
                      if (!uniqueNewLoans.has(m.newLoan.loanId)) {
                        uniqueNewLoans.set(m.newLoan.loanId, m.newLoan);
                      }
                    });
                    return Array.from(uniqueNewLoans.values()).map((loan, idx) => (
                      <div key={loan.loanId} className="text-sm mb-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">
                            {uniqueNewLoans.size > 1 && `${idx + 1}. `}
                            Loan <span className="text-white">{loan.loanId}</span>
                            <span className="text-gray-500 ml-1">
                              ({loan.lender || 'Unknown lender'})
                            </span>
                          </span>
                          <span className="text-blue-400 font-medium">
                            {formatCurrency(loan.loanAmountCents)}
                          </span>
                        </div>
                        {(loan.brokerDisplay || loan.officeName) && (
                          <div className="ml-4 text-xs text-gray-500">
                            {[loan.brokerDisplay, loan.officeName].filter(Boolean).join(' • ')}
                          </div>
                        )}
                        <div className="ml-4 text-xs text-green-400">
                          {formatCurrency(loan.commissionCents)} commission
                        </div>
                      </div>
                    ));
                  })()}
                  {primary.groupNewLoanCount > 1 && (
                    <div className="flex justify-between items-center pt-2 border-t border-gray-700 text-sm font-medium">
                      <span className="text-gray-300">Total New Loans</span>
                      <span className="text-blue-400">{formatCurrency(newLoanAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Details Row */}
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
              <span>
                Settled: {new Date(primary.newLoan.settlementDate).toLocaleDateString('en-AU')}
              </span>
              <span>{primary.daysBetween} days between</span>
              <span className="text-green-400 font-medium">
                {formatCurrency(primary.newLoan.commissionCents)} commission
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Section - always visible for pending groups */}
      {groupStatus === 'pending' && (
        <div className="border-t border-gray-700 p-4 bg-gray-850">
          <h4 className="text-sm font-semibold text-gray-300 mb-3">
            Verify Split Loan Group ({matches.length} portions)
          </h4>

          {/* Relationship - required choice, pre-selected from the detector's
              suggestion. Returning Client confirms carry no outcome type. */}
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1">Relationship</label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input
                  type="radio"
                  name={`relationship-${primary.splitGroupId}`}
                  value="refinance"
                  checked={selectedRelationship === 'refinance'}
                  onChange={() => onRelationshipChange('refinance')}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                Refinance
              </label>
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input
                  type="radio"
                  name={`relationship-${primary.splitGroupId}`}
                  value="returning_client"
                  checked={selectedRelationship === 'returning_client'}
                  onChange={() => onRelationshipChange('returning_client')}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                Returning Client
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {selectedRelationship === 'refinance' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Outcome Type</label>
                <select
                  value={selectedOutcome}
                  onChange={(e) => onOutcomeChange(e.target.value as OutcomeType)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="win_back">Win-Back (Same Broker)</option>
                  <option value="internal_transfer">Internal Transfer (Different Broker)</option>
                  <option value="cross_template_conversion">Cross-Loan Book Conversion</option>
                  <option value="office_transfer">Office Transfer (Same Broker)</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={verificationNotes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Add verification notes..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => onVerifyGroup(matches, 'confirmed')}
              disabled={isBrokerFilteredAdmin || isVerifying || !selectedRelationship}
              title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Confirm Split Loan
            </button>
            <button
              onClick={() => onVerifyGroup(matches, 'rejected')}
              disabled={isBrokerFilteredAdmin || isVerifying}
              title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              Reject All
            </button>
          </div>
        </div>
      )}

      {/* Verified Info */}
      {groupStatus !== 'pending' && primary.verifiedAt && (
        <div className="border-t border-gray-700 px-4 py-2 bg-gray-850 text-sm text-gray-400">
          Verified by {primary.verifiedByEmail} on {new Date(primary.verifiedAt).toLocaleDateString('en-AU')}
          {primary.outcomeType && (
            <span className="ml-2 text-cyan-400">
              ({primary.outcomeType.replace(/_/g, ' ')})
            </span>
          )}
        </div>
      )}

      {/* Reopen for Review - for rejected/confirmed split loan groups */}
      {groupStatus !== 'pending' && (
        <div className="border-t border-gray-700 p-4 bg-gray-850">
          {/* Show verification notes if any (especially auto-rejection reasons) */}
          {primary.verificationNotes && (
            <div className="mb-3 p-2 bg-gray-900 rounded text-sm">
              <span className="text-gray-500">Notes: </span>
              <span className="text-gray-300">{primary.verificationNotes}</span>
            </div>
          )}
          <button
            onClick={() => onVerifyGroup(matches, 'pending')}
            disabled={isBrokerFilteredAdmin || isVerifying}
            title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Reopen Group for Review
          </button>
        </div>
      )}

      {/* Contact History - available regardless of verification status. Anchored
          to the position-1 discharged loan of this split group. */}
      <div className="border-t border-gray-700 p-4 bg-gray-900/40">
        <button
          type="button"
          onClick={() => setContactsExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
        >
          {contactsExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <Phone className="w-3.5 h-3.5" />
          Contact History
          {contacts.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 bg-green-900 text-green-300 rounded-full text-[10px] font-medium">
              {contacts.length}
            </span>
          )}
        </button>
        {contactsExpanded && (
          <div className="mt-3">
            <p className="text-xs text-gray-500 mb-2">
              Contact history is anchored to the position-1 discharged loan
              (<span className="text-gray-300">{contactLoanId}</span>) for this split group.
            </p>
            <ContactTimeline
              companyId={companyId}
              loanId={contactLoanId}
              customerName={contactCustomerName}
              loanBroker={contactLoanBroker}
              sourcePage="Refinance Intelligence"
              entries={contacts}
              availableBrokers={availableBrokers}
              currentUser={currentUser}
              onMutate={onMutateContact}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Single Match Card
// ============================================================================

interface MatchCardProps {
  companyId: string;
  match: RefinanceMatch;
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedOutcome: OutcomeType;
  onOutcomeChange: (outcome: OutcomeType) => void;
  selectedRelationship: RelationshipType;
  onRelationshipChange: (relationship: RelationshipType) => void;
  verificationNotes: string;
  onNotesChange: (notes: string) => void;
  onVerify: (matchId: string, status: 'confirmed' | 'rejected' | 'pending') => void;
  onDelete: (matchId: string) => void;
  isVerifying: boolean;
  isDeleting: boolean;
  // Contact logging - anchored to the discharged loan of this match.
  contactLoanId: string;
  contactCustomerName?: string;
  contactLoanBroker?: string;
  contacts: LoanContactEntry[];
  onMutateContact: (input: MutateContactInput) => Promise<{ ok: boolean; entryId: string }>;
  currentUser: { uid?: string; displayName?: string; email?: string; role?: string };
  availableBrokers: { id: string; name: string }[];
}

const MatchCard: React.FC<MatchCardProps> = ({
  companyId,
  match,
  isExpanded,
  onToggleExpand,
  selectedOutcome,
  onOutcomeChange,
  selectedRelationship,
  onRelationshipChange,
  verificationNotes,
  onNotesChange,
  onVerify,
  onDelete,
  isVerifying,
  isDeleting,
  contactLoanId,
  contactCustomerName,
  contactLoanBroker,
  contacts,
  onMutateContact,
  currentUser,
  availableBrokers,
}) => {
  // Refinance verification/deletion is default-denied to a broker-
  // restricted admin at the callable chokepoint; disable, don't hide.
  const { isBrokerFilteredAdmin } = useAuth();
  const [contactsExpanded, setContactsExpanded] = useState(false);
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  const matchTypeColors = {
    exact: 'bg-green-500/20 text-green-400',
    normalized: 'bg-blue-500/20 text-blue-400',
    partial: 'bg-yellow-500/20 text-yellow-400',
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Card Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-750"
        onClick={onToggleExpand}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusColors[match.status]}`}>
                {match.status.toUpperCase()}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${matchTypeColors[match.matchType]}`}>
                {match.matchType} match
              </span>
              {match.isAcquiredToPrimary && match.status === 'confirmed' && (
                <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs font-medium flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Acquired to Primary
                </span>
              )}
              <span className="px-2 py-0.5 bg-gray-700 text-gray-300 rounded text-xs">
                {match.confidenceScore}% confidence
              </span>
            </div>

            {/* Customer Names */}
            <div className="flex items-center gap-3 text-lg">
              <span className="font-medium text-white">{match.discharged.customerName}</span>
              <ArrowRight className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-white">{match.newLoan.customerName}</span>
            </div>

            {/* Loan Book Flow with Broker & Office */}
            <div className="flex items-center gap-2 mt-2 text-sm flex-wrap">
              <span className="text-gray-400">
                {match.discharged.templateName || 'Unknown Loan Book'}
                {(match.discharged.brokerDisplay || match.discharged.officeName) && (
                  <span className="text-gray-500 ml-1">
                    ({[match.discharged.brokerDisplay, match.discharged.officeName].filter(Boolean).join(' - ')})
                  </span>
                )}
              </span>
              <ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <span className="text-cyan-400 font-medium">
                {match.newLoan.templateName || 'Unknown Loan Book'}
                {(match.newLoan.brokerDisplay || match.newLoan.officeName) && (
                  <span className="text-cyan-300/70 ml-1">
                    ({[match.newLoan.brokerDisplay, match.newLoan.officeName].filter(Boolean).join(' - ')})
                  </span>
                )}
              </span>
            </div>

            {/* Loan Details - Discharged and New */}
            <div className="mt-2 text-sm space-y-1">
              <div className="text-gray-400">
                <span className="text-gray-500">Discharged:</span>{' '}
                <span className="text-white font-medium">{match.discharged.loanId}</span>
                <span className="mx-1 text-gray-500">•</span>
                <span className="text-gray-400">{match.discharged.lender || 'Unknown Lender'}</span>
              </div>
              <div className="text-gray-400">
                <span className="text-gray-500">New Loan:</span>{' '}
                <span className="text-white font-medium">{match.newLoan.loanId}</span>
                <span className="mx-1 text-gray-500">•</span>
                <span className="text-blue-400">{match.newLoan.lender || 'Unknown Lender'}</span>
              </div>
            </div>

            {/* Loan Amount Comparison */}
            {(match.discharged.balanceAtDischargeCents || match.newLoan.loanAmountCents > 0) && (
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="text-gray-500">
                  {match.discharged.balanceAtDischargeCents
                    ? formatCurrency(match.discharged.balanceAtDischargeCents)
                    : 'Unknown'
                  }
                </span>
                <ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                <span className="text-blue-400 font-medium">
                  {formatCurrency(match.newLoan.loanAmountCents)}
                </span>
                {match.loanValueChangePercent !== null && match.loanValueChangePercent !== undefined && (
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    match.loanValueChangePercent >= 0
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {match.loanValueChangePercent >= 0 ? '+' : ''}{match.loanValueChangePercent.toFixed(1)}%
                  </span>
                )}
              </div>
            )}

            {/* Details Row */}
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
              <span>
                Discharged: {new Date(match.discharged.dischargeDate).toLocaleDateString('en-AU')}
              </span>
              <span>
                Settled: {new Date(match.newLoan.settlementDate).toLocaleDateString('en-AU')}
              </span>
              <span>{match.daysBetween} days between</span>
              <span className="text-green-400 font-medium">
                {formatCurrency(match.newLoan.commissionCents)} commission
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Section - always visible for pending matches */}
      {match.status === 'pending' && (
        <div className="border-t border-gray-700 p-4 bg-gray-850">
          <h4 className="text-sm font-semibold text-gray-300 mb-3">Verify Match</h4>

          {/* Relationship - required choice, pre-selected from the detector's
              suggestion. Returning Client confirms carry no outcome type. */}
          <div className="mb-4">
            <label className="block text-xs text-gray-400 mb-1">Relationship</label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input
                  type="radio"
                  name={`relationship-${match.matchId}`}
                  value="refinance"
                  checked={selectedRelationship === 'refinance'}
                  onChange={() => onRelationshipChange('refinance')}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                Refinance
              </label>
              <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
                <input
                  type="radio"
                  name={`relationship-${match.matchId}`}
                  value="returning_client"
                  checked={selectedRelationship === 'returning_client'}
                  onChange={() => onRelationshipChange('returning_client')}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                Returning Client
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {selectedRelationship === 'refinance' && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Outcome Type</label>
                <select
                  value={selectedOutcome}
                  onChange={(e) => onOutcomeChange(e.target.value as OutcomeType)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="win_back">Win-Back (Same Broker)</option>
                  <option value="internal_transfer">Internal Transfer (Different Broker)</option>
                  <option value="cross_template_conversion">Cross-Loan Book Conversion</option>
                  <option value="office_transfer">Office Transfer (Same Broker)</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={verificationNotes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Add verification notes..."
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => onVerify(match.matchId, 'confirmed')}
              disabled={isBrokerFilteredAdmin || isVerifying || !selectedRelationship}
              title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
              className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Confirm Match
            </button>
            <button
              onClick={() => onVerify(match.matchId, 'rejected')}
              disabled={isBrokerFilteredAdmin || isVerifying}
              title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
              className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              Reject Match
            </button>
          </div>
        </div>
      )}

      {/* Verified Info */}
      {match.status !== 'pending' && match.verifiedAt && (
        <div className="border-t border-gray-700 px-4 py-2 bg-gray-850 text-sm text-gray-400">
          Verified by {match.verifiedByEmail} on {new Date(match.verifiedAt).toLocaleDateString('en-AU')}
          {match.outcomeType && (
            <span className="ml-2 text-cyan-400">
              ({match.outcomeType.replace(/_/g, ' ')})
            </span>
          )}
        </div>
      )}

      {/* Reopen/Delete - for rejected/confirmed matches */}
      {match.status !== 'pending' && (
        <div className="border-t border-gray-700 p-4 bg-gray-850">
          {/* Show verification notes if any (especially auto-rejection reasons) */}
          {match.verificationNotes && (
            <div className="mb-3 p-2 bg-gray-900 rounded text-sm">
              <span className="text-gray-500">Notes: </span>
              <span className="text-gray-300">{match.verificationNotes}</span>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => onVerify(match.matchId, 'pending')}
              disabled={isBrokerFilteredAdmin || isVerifying || isDeleting}
              title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
              className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              Reopen for Review
            </button>
            <button
              onClick={() => onDelete(match.matchId)}
              disabled={isBrokerFilteredAdmin || isVerifying || isDeleting}
              title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : 'Delete match and re-run detection to create fresh match with updated data'}
              className="px-4 py-2 bg-red-800 hover:bg-red-900 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete & Re-detect
            </button>
          </div>
        </div>
      )}

      {/* Contact History - available regardless of verification status. Anchored
          to the discharged loan of this match. */}
      <div className="border-t border-gray-700 p-4 bg-gray-900/40">
        <button
          type="button"
          onClick={() => setContactsExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
        >
          {contactsExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <Phone className="w-3.5 h-3.5" />
          Contact History
          {contacts.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 bg-green-900 text-green-300 rounded-full text-[10px] font-medium">
              {contacts.length}
            </span>
          )}
        </button>
        {contactsExpanded && (
          <div className="mt-3">
            <ContactTimeline
              companyId={companyId}
              loanId={contactLoanId}
              customerName={contactCustomerName}
              loanBroker={contactLoanBroker}
              sourcePage="Refinance Intelligence"
              entries={contacts}
              availableBrokers={availableBrokers}
              currentUser={currentUser}
              onMutate={onMutateContact}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RefinanceIntelligencePage;
